import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, AlertCircle, FileText, Calendar, Lock, Mail, Loader2, CheckCircle, X } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert.js';

// API requests go through CloudFront
const API_BASE_URL = '';
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB

const UploadPage = () => {
  const { requestId } = useParams();
  const [requestInfo, setRequestInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageError, setPageError] = useState('');

  // Auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [uploadToken, setUploadToken] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState([]);

  useEffect(() => {
    const fetchRequestInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/receive/${requestId}`);

        if (response.status === 404) {
          setPageError('アップロードリクエストが見つかりません。URLをご確認ください。');
          return;
        }

        if (response.status === 410) {
          const data = await response.json();
          if (data.status === 'received') {
            setPageError('このリクエストにはすでにファイルがアップロードされています。');
          } else {
            setPageError('このアップロードリクエストの有効期限が切れています。');
          }
          return;
        }

        if (!response.ok) {
          throw new Error('リクエスト情報の取得に失敗しました');
        }

        const data = await response.json();
        setRequestInfo(data);
      } catch (err) {
        console.error('Error:', err);
        setPageError('リクエスト情報の取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setLoading(false);
      }
    };

    fetchRequestInfo();
  }, [requestId]);

  const formatFileSize = (bytes) => {
    if (bytes == null) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/receive/${requestId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 403) {
        const data = await response.json();
        if (data.error === 'Invalid email domain') {
          setError(`メールアドレスのドメインが正しくありません。@${requestInfo.emailDomain} のアドレスをご使用ください。`);
        } else if (data.error === 'Invalid password') {
          setError('パスワードが正しくありません。');
        } else {
          setError('認証に失敗しました。');
        }
        return;
      }

      if (response.status === 410) {
        setPageError('このアップロードリクエストの有効期限が切れています。');
        return;
      }

      if (!response.ok) {
        throw new Error('認証に失敗しました');
      }

      const data = await response.json();
      setUploadToken(data.uploadToken);
      setAuthenticated(true);
    } catch (err) {
      console.error('Verify error:', err);
      setError('認証に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setVerifying(false);
    }
  };

  const handleFiles = useCallback((fileList) => {
    setError('');
    const newFiles = Array.from(fileList);
    const validFiles = [];

    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name}: ファイルサイズは5GB以下にしてください。`);
        continue;
      }
      // Dedup check
      if (!selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, [selectedFiles]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const uploadSingleFile = async (file, fileIndex, totalFiles, isLastFile) => {
    // Initiate upload
    const initResponse = await fetch(`${API_BASE_URL}/receive/${requestId}/initiate-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadToken,
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        fileSize: file.size,
      }),
    });

    if (!initResponse.ok) {
      const errData = await initResponse.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to initiate upload');
    }

    const { uploadId, s3Key, signedUrls, totalParts, chunkSize } = await initResponse.json();

    // Upload parts
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const url = signedUrls[partNumber.toString()];

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: chunk,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload part ${partNumber}`);
      }

      // Update progress
      const fileProgress = (partNumber / totalParts) * 100;
      const overallProgress = ((fileIndex + fileProgress / 100) / totalFiles) * 100;
      setProgress(overallProgress);
    }

    // Complete upload
    const completeResponse = await fetch(`${API_BASE_URL}/receive/${requestId}/complete-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadToken,
        s3Key,
        uploadId,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream',
        isLastFile: isLastFile,
      }),
    });

    if (!completeResponse.ok) {
      throw new Error('Failed to complete upload');
    }

    return { name: file.name, s3Key };
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError('');
    setProgress(0);

    const totalFiles = selectedFiles.length;
    const successNames = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      try {
        const isLastFile = (i === selectedFiles.length - 1);
        const result = await uploadSingleFile(selectedFiles[i], i, totalFiles, isLastFile);
        successNames.push(result.name);
      } catch (err) {
        console.error(`Upload failed for ${selectedFiles[i].name}:`, err);
        setError(`${selectedFiles[i].name} のアップロードに失敗しました: ${err.message}`);
        setUploading(false);
        return;
      }
    }

    setProgress(100);
    setUploadedFileNames(successNames);
    setUploadComplete(true);
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5a79ba]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-[800px]">
        <img
          className="mx-auto h-12 w-auto"
          src={`${process.env.PUBLIC_URL}/images/Brains_logo.png`}
          alt="Brains Logo"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
          ファイルアップロード
        </h2>
        <div className="mt-2 flex justify-center gap-8">
          <div className="text-center">
            <p className="text-sm text-gray-500">企業内全文検索エンジン</p>
            <p className="text-2xl font-bold text-[#5a79ba]">Neuron ES</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">AI異常検知ソリューション</p>
            <p className="text-2xl font-bold text-[#5a79ba]">Impulse</p>
          </div>
        </div>
      </div>

      <div className="mt-8 mx-auto w-full max-w-[800px]">
        <div className="bg-white py-8 px-4 shadow-sm border border-gray-200/60 rounded-2xl sm:px-10">
          {pageError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{pageError}</AlertDescription>
            </Alert>
          ) : uploadComplete ? (
            /* Upload Complete */
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                アップロードが完了しました
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                ファイルが正常にアップロードされました。ありがとうございます。
              </p>

              <div className="bg-gray-50/80 border border-gray-200/60 rounded-xl p-4 text-left">
                <span className="text-sm font-semibold text-gray-700">
                  アップロードしたファイル（{uploadedFileNames.length}件）
                </span>
                <ul className="mt-2 space-y-1.5">
                  {uploadedFileNames.map((name, index) => (
                    <li key={index} className="flex items-center text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : !authenticated ? (
            /* Authentication Form */
            <>
              {requestInfo && (
                <div className="mb-6 text-center">
                  <p className="text-sm text-gray-500">
                    <strong className="text-gray-700">{requestInfo.customerName}</strong> 様へのファイル受領リクエスト
                  </p>
                  <div className="flex items-center justify-center text-sm text-gray-500 mt-2">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>有効期限: <strong className="text-gray-700">{requestInfo.expiresAt}</strong></span>
                  </div>
                </div>
              )}

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    <Mail className="h-4 w-4 inline mr-1" />
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={requestInfo ? `your@${requestInfo.emailDomain}` : 'your@email.com'}
                    className="mt-1 block w-full px-3.5 py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5a79ba]/30 focus:border-[#5a79ba] sm:text-sm transition-all duration-200"
                    required
                  />
                  {requestInfo?.emailDomain && (
                    <p className="mt-1 text-xs text-gray-400">
                      @{requestInfo.emailDomain} ドメインのメールアドレスをご使用ください
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    <Lock className="h-4 w-4 inline mr-1" />
                    パスワード
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    className="mt-1 block w-full px-3.5 py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5a79ba]/30 focus:border-[#5a79ba] sm:text-sm transition-all duration-200"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={verifying || !email || !password}
                  className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-[#5a79ba] hover:bg-[#46639e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5a79ba] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      認証中...
                    </>
                  ) : (
                    '認証してアップロード画面へ'
                  )}
                </button>
              </form>
            </>
          ) : (
            /* File Upload Form */
            <>
              {requestInfo && (
                <div className="mb-6 text-center">
                  <p className="text-sm text-gray-500">
                    <strong className="text-gray-700">{requestInfo.customerName}</strong> 様 - ファイルアップロード
                  </p>
                </div>
              )}

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Drag & Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center mb-4 transition-all duration-200 ${
                  dragActive
                    ? 'border-[#5a79ba] bg-[#8499c8]/10 scale-[1.01]'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center justify-center"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#8499c8]/12 flex items-center justify-center mb-4">
                    <Upload className="h-7 w-7 text-[#5a79ba]" />
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    ドラッグ＆ドロップ、<br/>または
                    <span className="font-semibold text-[#5a79ba] hover:text-[#46639e] ml-1">
                      クリックしてファイルを選択
                    </span>
                  </p>
                  <p className="mt-2 text-xs text-gray-400">
                    全ファイル種類対応（複数選択可・最大5GB/ファイル）
                  </p>
                </label>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="bg-gray-50/80 border border-gray-200/60 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-700">
                      選択中のファイル（{selectedFiles.length}件）
                    </span>
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
                    >
                      すべて削除
                    </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white border border-gray-200/60 p-2.5 rounded-lg"
                      >
                        <div className="flex items-center min-w-0">
                          <FileText className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-gray-400 hover:text-red-500 ml-2 p-1 flex-shrink-0 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress */}
              {uploading && (
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#5a79ba] to-[#8499c8] h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2 text-center font-medium">
                    アップロード進捗: {progress.toFixed(0)}%
                  </p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || uploading}
                className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-[#5a79ba] hover:bg-[#46639e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5a79ba] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    アップロード中...
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    アップロード（{selectedFiles.length}件）
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
